import React from 'react'
import i18next from 'i18next'

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info' | 'neutral'
type BadgeSize = 'sm' | 'md'

interface BadgeProps {
  children: React.ReactNode
  variant?: BadgeVariant
  size?: BadgeSize
  className?: string
  dot?: boolean
}

const variantStyles: Record<BadgeVariant, string> = {
  default: 'bg-indigo-100 text-indigo-800',
  success: 'bg-green-100 text-green-800',
  warning: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-800',
  info: 'bg-blue-100 text-blue-800',
  neutral: 'bg-gray-100 text-gray-800',
}

const sizeStyles: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-xs',
}

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  className = '',
  dot = false,
}: BadgeProps) {
  return (
    <span
      className={`
        inline-flex items-center rounded-full font-medium whitespace-nowrap
        ${variantStyles[variant]}
        ${sizeStyles[size]}
        ${className}
      `}
    >
      {dot && (
        <span
          className={`
            w-1.5 h-1.5 rounded-full mr-1.5
            ${variant === 'success' ? 'bg-green-500' : ''}
            ${variant === 'warning' ? 'bg-amber-500' : ''}
            ${variant === 'error' ? 'bg-red-500' : ''}
            ${variant === 'info' ? 'bg-blue-500' : ''}
            ${variant === 'default' ? 'bg-indigo-500' : ''}
            ${variant === 'neutral' ? 'bg-gray-500' : ''}
          `}
        />
      )}
      {children}
    </span>
  )
}

interface StatusBadgeProps {
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'failed' | string
  label?: string
}

const statusMapping: Record<string, { variant: BadgeVariant; key: string }> = {
  pending: { variant: 'warning', key: 'status.pending' },
  waiting: { variant: 'warning', key: 'status.pending' },
  odottaa: { variant: 'warning', key: 'status.pending' },
  approved: { variant: 'success', key: 'status.approved' },
  completed: { variant: 'success', key: 'status.completed' },
  successful: { variant: 'success', key: 'status.successful' },
  hyväksytty: { variant: 'success', key: 'status.approved' },
  rejected: { variant: 'error', key: 'status.rejected' },
  failed: { variant: 'error', key: 'status.failed' },
  hylätty: { variant: 'error', key: 'status.rejected' },
}

export function StatusBadge({ status, label }: StatusBadgeProps) {
  const mapping = statusMapping[status.toLowerCase()]
  const variant = mapping?.variant || 'neutral'
  const resolvedLabel = label || (mapping ? i18next.t(mapping.key) : status)
  return <Badge variant={variant}>{resolvedLabel}</Badge>
}

interface ConnectionBadgeProps {
  connected: boolean
  connectedLabel?: string
  disconnectedLabel?: string
}

export function ConnectionBadge({
  connected,
  connectedLabel = 'Yhdistetty',
  disconnectedLabel = 'Ei yhdistetty',
}: ConnectionBadgeProps) {
  return (
    <Badge variant={connected ? 'success' : 'neutral'} dot>
      {connected ? connectedLabel : disconnectedLabel}
    </Badge>
  )
}
